// ─────────────────────────────────────────────────────────────────────────────
// Process Manufacturing — Batch Operations Page
// Real-time temperature/pressure readings, labor hours, energy consumption logs
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/Select';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/Tabs';
import { Search, Activity, Thermometer, Clock, Zap, ChevronRight } from 'lucide-react';
import { useApi } from '@/shared/hooks/useApi';
import { formatINR } from '@/shared/utils/currency';

interface BatchOperation {
  _id: string;
  orderId: string;
  batchNumber: string;
  operationReadings: Array<{
    lineNumber: number;
    operationCode: string;
    operationName: string;
    readings: Array<{
      timestamp: string;
      recordedBy: string;
      temperature: number;
      pressure: number;
      flowRate: number;
      level: number;
      vibration: number;
      powerConsumption: number;
      notes: string;
    }>;
    actualSetupTime: number;
    actualRunTime: number;
    actualLaborHours: number;
    actualLaborRate: number;
    actualMachineHours: number;
    actualMachineRate: number;
    actualOverhead: number;
    energyConsumed: number;
    energyCost: number;
    status: string;
  }>;
  status: string;
}

export function BatchOperationsPage() {
  const [batches, setBatches] = useState<BatchOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedBatch, setSelectedBatch] = useState<BatchOperation | null>(null);
  const [activeTab, setActiveTab] = useState<'readings' | 'labor' | 'energy'>('readings');
  const { get } = useApi();
  
  useEffect(() => {
    fetchBatches();
  }, [get]);
  
  async function fetchBatches() {
    setLoading(true);
    try {
      await get('/api/manufacturing/orders?status=in_progress&status=completed');
      // In production, this would fetch batch records with operation readings
      setBatches([]);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    } finally {
      setLoading(false);
    }
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Batch Monitoring</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Real-time temperature/pressure readings, labor hours, energy consumption logs</p>
        </div>
      </div>
      
      <Card>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-4">
            <Input placeholder="Search batches..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" leftIcon={<Search className="w-4 h-4" />} />
          </div>
        </div>
        
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : batches.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">No active batches</h3>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Start a production order to see batch operations here</p>
              <Button variant="outline" className="mt-4" onClick={() => window.location.href = '/manufacturing/orders'}>
                <ChevronRight className="w-4 h-4 mr-2" />
                View Production Orders
              </Button>
            </div>
          ) : (
            <>
              {/* Batch Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Select Batch</label>
                <Select value={selectedBatch?._id || ''} onValueChange={v => setSelectedBatch(batches.find(b => b._id === v) || null)}>
                  <SelectTrigger className="w-80"><SelectValue placeholder="Choose a batch..." /></SelectTrigger>
                  <SelectContent>
                    {batches.map(batch => (
                      <SelectItem key={batch._id} value={batch._id}>{batch.batchNumber} - {batch.orderId}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedBatch && (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="readings"><Thermometer className="w-4 h-4 mr-2" /> Readings</TabsTrigger>
                    <TabsTrigger value="labor"><Clock className="w-4 h-4 mr-2" /> Labor & Machine</TabsTrigger>
                    <TabsTrigger value="energy"><Zap className="w-4 h-4 mr-2" /> Energy</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="readings" className="p-4 space-y-6">
                    {selectedBatch.operationReadings.map((op, opIdx) => (
                      <Card key={opIdx} className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-medium">{op.operationCode} - {op.operationName}</h4>
                          <Badge variant={op.status === 'completed' ? 'success' : 'secondary'}>{op.status}</Badge>
                        </div>
                        
                        {op.readings.length > 0 && (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Timestamp</TableHead>
                                <TableHead>Recorded By</TableHead>
                                <TableHead>Temp (°C)</TableHead>
                                <TableHead>Pressure (bar)</TableHead>
                                <TableHead>Flow Rate</TableHead>
                                <TableHead>Level (mm)</TableHead>
                                <TableHead>Vibration (mm/s)</TableHead>
                                <TableHead>Power (kW)</TableHead>
                                <TableHead>Notes</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {op.readings.map((r, rIdx) => (
                                <TableRow key={rIdx}>
                                  <TableCell className="text-sm">{new Date(r.timestamp).toLocaleString()}</TableCell>
                                  <TableCell>{r.recordedBy}</TableCell>
                                  <TableCell>{r.temperature !== null ? r.temperature.toFixed(1) : '-'}</TableCell>
                                  <TableCell>{r.pressure !== null ? r.pressure.toFixed(2) : '-'}</TableCell>
                                  <TableCell>{r.flowRate !== null ? r.flowRate.toLocaleString() : '-'}</TableCell>
                                  <TableCell>{r.level !== null ? r.level : '-'}</TableCell>
                                  <TableCell>{r.vibration !== null ? r.vibration.toFixed(1) : '-'}</TableCell>
                                  <TableCell>{r.powerConsumption !== null ? r.powerConsumption.toFixed(1) : '-'}</TableCell>
                                  <TableCell className="max-w-xs truncate">{r.notes}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Card>
                    ))}
                  </TabsContent>
                  
                  <TabsContent value="labor" className="p-4 space-y-6">
                    {selectedBatch.operationReadings.map((op, opIdx) => (
                      <Card key={opIdx} className="p-4">
                        <h4 className="font-medium mb-4">{op.operationCode} - {op.operationName}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Setup Time</p>
                            <p className="text-2xl font-bold">{op.actualSetupTime} min</p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Run Time</p>
                            <p className="text-2xl font-bold">{op.actualRunTime} min</p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Labor Hours</p>
                            <p className="text-2xl font-bold">{op.actualLaborHours} hrs</p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Labor Cost</p>
                            <p className="text-2xl font-bold">{formatINR(op.actualLaborHours * op.actualLaborRate)}</p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Machine Hours</p>
                            <p className="text-2xl font-bold">{op.actualMachineHours} hrs</p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Machine Cost</p>
                            <p className="text-2xl font-bold">{formatINR(op.actualMachineHours * op.actualMachineRate)}</p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Overhead</p>
                            <p className="text-2xl font-bold">{formatINR(op.actualOverhead)}</p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Total Op Cost</p>
                            <p className="text-2xl font-bold">
                              {formatINR(op.actualLaborHours * op.actualLaborRate + op.actualMachineHours * op.actualMachineRate + op.actualOverhead)}
                            </p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </TabsContent>
                  
                  <TabsContent value="energy" className="p-4 space-y-6">
                    {selectedBatch.operationReadings.map((op, opIdx) => (
                      <Card key={opIdx} className="p-4">
                        <h4 className="font-medium mb-4">{op.operationCode} - {op.operationName}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Energy Consumed</p>
                            <p className="text-2xl font-bold">{op.energyConsumed.toLocaleString()} kWh</p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Energy Cost</p>
                            <p className="text-2xl font-bold">{formatINR(op.energyCost)}</p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Cost/kWh</p>
                            <p className="text-2xl font-bold">
                              {op.energyConsumed > 0 ? (op.energyCost / op.energyConsumed).toFixed(2) : 0}
                            </p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                            <p className="text-sm text-gray-500">Duration</p>
                            <p className="text-2xl font-bold">
                              {((op.actualSetupTime + op.actualRunTime) / 60).toFixed(1)} hrs
                            </p>
                          </div>
                        </div>
                      </Card>
                    ))}
                    <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
                      <h4 className="font-medium mb-4">Batch Energy Summary</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 bg-white dark:bg-gray-800 rounded">
                          <p className="text-sm text-gray-500">Total Energy</p>
                          <p className="text-2xl font-bold">
                            {selectedBatch.operationReadings.reduce((sum, o) => sum + o.energyConsumed, 0).toLocaleString()} kWh
                          </p>
                        </div>
                        <div className="p-3 bg-white dark:bg-gray-800 rounded">
                          <p className="text-sm text-gray-500">Total Energy Cost</p>
                          <p className="text-2xl font-bold">
                            {formatINR(selectedBatch.operationReadings.reduce((sum, o) => sum + o.energyCost, 0))}
                          </p>
                        </div>
                        <div className="p-3 bg-white dark:bg-gray-800 rounded">
                          <p className="text-sm text-gray-500">Avg Cost/kWh</p>
                          <p className="text-2xl font-bold">
                            {(() => {
                              const totalE = selectedBatch.operationReadings.reduce((sum, o) => sum + o.energyConsumed, 0);
                              const totalC = selectedBatch.operationReadings.reduce((sum, o) => sum + o.energyCost, 0);
                              return totalE > 0 ? (totalC / totalE).toFixed(2) : 0;
                            })()}
                          </p>
                        </div>
                        <div className="p-3 bg-white dark:bg-gray-800 rounded">
                          <p className="text-sm text-gray-500">Operations</p>
                          <p className="text-2xl font-bold">{selectedBatch.operationReadings.length}</p>
                        </div>
                      </div>
                    </Card>
                  </TabsContent>
                </Tabs>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}